import { useQuery } from '@tanstack/react-query';

import { companyApi } from '@/api';

/**
 * Whether this company may post internships and grow its team.
 *
 * Both wait for an admin to verify the company; the API refuses them with
 * `COMPANY_NOT_VERIFIED` until then, and the gate components say so up front
 * instead of letting somebody fill in a form that cannot be saved. Read from
 * the company profile, which every member can load (the verification endpoint
 * is for those who may act on it).
 *
 * `needsVerification` is only true once the status is known, so a verified
 * company never sees the gate flash while the profile loads.
 */
export function useCompanyVerification() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['company-profile'],
    queryFn: companyApi.getProfile,
    staleTime: 60_000,
  });
  const status = data?.verification_status ?? null;
  const isKnown = !isPending && !isError && status !== null;
  return {
    status,
    // What the admin wrote when turning the request down; null otherwise.
    rejectionReason: status === 'REJECTED' ? (data?.verification_rejection_reason ?? null) : null,
    isVerified: status === 'VERIFIED',
    needsVerification: isKnown && status !== 'VERIFIED',
    // The company's own logo, for the sidebar; null until one is uploaded.
    logoUrl: data?.logo_url ?? null,
  };
}
